import { createApi } from '@reduxjs/toolkit/query/react';

import { DeleteParamsDto, DeleteStatusDto, PaginatedResponse, StatusDto } from '../common/dto/common.dto';
import { FilterDto } from '../common/dto/filter.dto';
import { LocationLinkDto, RemoveLocationLinkDto } from '../common/dto/location.dto';
import { CreatedUserWithUsernameResponseDto, UserCreateDto, UserProfileUpdateDto, UserUpdateDto, UserViewDto } from '../common/dto/user.dto';
import customFetchBase from './base.service';

const API_URI = import.meta.env.VITE_CORE_SERVICE;
const TAG_TYPE = 'User';

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: customFetchBase(`${API_URI}/api/user`),
  tagTypes: [TAG_TYPE],
  endpoints: (builder) => ({
    // getUsersList: builder.query<PaginatedResponse<UserViewDto>, ListCommonPayload>({
    //   query: (payload: ListCommonPayload) => {
    //     let query = 'user/?';
    //     if (payload.searchParam) query += `searchParam=${payload.searchParam}&`;
    //     if (payload.page) query += `page=${payload.page}&`;
    //     if (payload.limit) query += `limit=${payload.limit}&`;
    //     if (payload.disablePagination) query += `disablePagination=${payload.disablePagination}&`;
    //     if (payload.sortBy) query += `sortBy=${payload.sortBy}&`;
    //     if (payload.sortOrder) query += `sortOrder=${payload.sortOrder}`;
    //     return query;
    //   },
    //   providesTags: [TAG_TYPE],
    // }),
    searchUser: builder.mutation<PaginatedResponse<UserViewDto>, Partial<FilterDto>>({
      query(body) {
        return {
          url: ``,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    getUser: builder.query<UserViewDto, string>({
      query: (id: string) => {
        return `${id}`;
      },
      providesTags: [TAG_TYPE],
    }),
    updateUser: builder.mutation<UserViewDto, Partial<UserUpdateDto>>({
      query(data) {
        return {
          url: ``,
          method: 'PUT',
          body: data,
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    updateUserProfile: builder.mutation<UserViewDto, Partial<UserProfileUpdateDto>>({
      query(data) {
        return {
          url: `/update-profile`,
          method: 'PUT',
          body: data,
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    resetUserPassword: builder.mutation<CreatedUserWithUsernameResponseDto, Partial<string>>({
      query(id: string) {
        return {
          url: `/reset-password/${id}`,
          method: 'PUT',
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    resetUserMFA: builder.mutation<UserViewDto, Partial<string>>({
      query(id: string) {
        return {
          url: `/reset-mfa/${id}`,
          method: 'PUT',
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    addUserWithEmail: builder.mutation<UserViewDto, Omit<UserCreateDto, 'login'> & { email: string }>({
      query(body) {
        return {
          url: `/with-email`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    addUserWithUsername: builder.mutation<CreatedUserWithUsernameResponseDto, Omit<UserCreateDto, 'login'> & { username: string }>({
      query(body) {
        return {
          url: `/with-username`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    deleteUser: builder.mutation<DeleteStatusDto, DeleteParamsDto>({
      query(data: DeleteParamsDto) {
        return {
          url: data.id,
          method: 'DELETE',
          body: data.params ?? {},
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    addUsersToLocation: builder.mutation<StatusDto, LocationLinkDto>({
      query(body) {
        return {
          url: `addUsersToLocation`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    reinviteUser: builder.mutation<StatusDto, Partial<string>>({
      query(id: string) {
        return {
          url: `/re-invite/${id}`,
          method: 'PUT',
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    removeUserFromLocation: builder.mutation<StatusDto, RemoveLocationLinkDto>({
      query(body) {
        return {
          url: `removeUserFromLocation`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
  }),
});

// auto-generated based on the defined endpoints
export const {
  useGetUserQuery,
  useUpdateUserMutation,
  useUpdateUserProfileMutation,
  useResetUserPasswordMutation,
  useResetUserMFAMutation,
  useAddUserWithEmailMutation,
  useAddUserWithUsernameMutation,
  useSearchUserMutation,
  useDeleteUserMutation,
  useAddUsersToLocationMutation,
  useRemoveUserFromLocationMutation,
  useReinviteUserMutation,
} = userApi;
